import { useMemo } from "react";
import { MapPin } from "lucide-react";
import StepShell from "./StepShell";
import { OHIO_COUNTIES, OHIO_TAX_EFFECTIVE_DATE, OHIO_TAX_PDF, computeOhioTax } from "../../data/finance";
import { fmtMoney } from "../../config";

/**
 * Step 10 — Sales Tax / County.
 *
 * Restored from PaymentCalculator. Ohio sales tax depends on the
 * customer's county of registration. We show every Ohio county with its
 * current rate, default to Franklin (Scott's home county), and show a
 * live tax estimate using the same `computeOhioTax()` helper from
 * /src/data/finance.js — math is UNTOUCHED.
 *
 * Ohio rule (also enforced in finance.js):
 *   taxable = max(0, selling_price − eligible_trade_allowance)
 *   tax     = taxable × county_rate
 *
 * Rebates and cash down DO NOT reduce the taxable base under Ohio law.
 */
export default function SalesTaxStep({ byod, vehicle, onContinue, onBack, firstName }) {
  const cur = byod.state.sales_tax?.county || "Franklin";
  const setCounty = (county) => byod.update("sales_tax", { county });

  const sortedCounties = useMemo(
    () => [...OHIO_COUNTIES].sort((a, b) => a.name.localeCompare(b.name)),
    []
  );

  // Live tax preview using current state
  const preview = useMemo(() => {
    const price = Number(vehicle?.price || 0);
    const tradeValue = byod.state.trade?.has_trade === "yes" ? byod.tradeSelectedValue : 0;
    const row = OHIO_COUNTIES.find((c) => c.name === cur) || OHIO_COUNTIES[0];
    const rate = row.rate;
    const tax = price > 0 ? computeOhioTax({ price, tradeValue, rebates: 0, rate }) : 0;
    const taxable = Math.max(0, price - tradeValue);
    return { rate, tax, taxable, row };
  }, [vehicle, cur, byod.state.trade, byod.tradeSelectedValue]);

  return (
    <StepShell
      title={firstName ? `${firstName}, where will you register the vehicle?` : "Where will you register the vehicle?"}
      subtitle="Ohio sales tax is set by the county where the vehicle is titled. Pick yours below so the estimate is accurate."
      onBack={onBack}
      onContinue={onContinue}
      testIdPrefix="byod-sales-tax-step"
    >
      <div className="grid md:grid-cols-[260px,1fr] gap-4">
        <div className="rounded-2xl border-2 border-[var(--ssc-line)] bg-white p-4">
          <label htmlFor="byod-county-select" className="block">
            <span className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)] flex items-center gap-1">
              <MapPin size={12} /> Ohio county
            </span>
            <select
              id="byod-county-select"
              value={cur}
              onChange={(e) => setCounty(e.target.value)}
              className="ssc-select mt-2 w-full"
              data-testid="byod-sales-tax-county"
            >
              {sortedCounties.map((c) => (
                <option key={c.name} value={c.name}>{c.name} · {(c.rate * 100).toFixed(2)}%</option>
              ))}
            </select>
          </label>
          <p className="text-[11px] text-[var(--ssc-ink-2)] mt-3 leading-snug">
            Rates effective {OHIO_TAX_EFFECTIVE_DATE}. <a href={OHIO_TAX_PDF} target="_blank" rel="noopener noreferrer" className="underline">View official Ohio rate map</a>.
          </p>
        </div>

        <div className="rounded-2xl border-2 border-[var(--ssc-blue)] bg-[var(--ssc-blue)]/[0.04] p-4" data-testid="byod-sales-tax-preview">
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">Estimated sales tax</div>
          <div className="text-3xl font-black text-[var(--ssc-navy)] mt-1">{fmtMoney(preview.tax)}</div>
          <div className="text-xs text-[var(--ssc-ink-2)] mt-1">
            {cur} County · <strong>{(preview.rate * 100).toFixed(2)}%</strong>
            {byod.state.trade?.has_trade === "yes" && byod.tradeSelectedValue > 0 ? ` · trade reduces taxable amount` : ""}
          </div>
          <dl className="mt-4 text-sm space-y-1">
            <Row label="Selling price" value={fmtMoney(Number(vehicle?.price || 0))} />
            {byod.tradeSelectedValue > 0 && byod.state.trade?.has_trade === "yes" && (
              <Row label="Trade allowance" value={`− ${fmtMoney(byod.tradeSelectedValue)}`} />
            )}
            <Row label="Taxable amount" value={fmtMoney(preview.taxable)} bold />
            <Row label={`Tax (${(preview.rate * 100).toFixed(2)}%)`} value={fmtMoney(preview.tax)} bold />
          </dl>
          <p className="text-[11px] text-[var(--ssc-ink-2)] mt-3 leading-snug">
            Under Ohio law, rebates and cash down DO NOT reduce the taxable amount — only an eligible trade allowance does.
            This is a guesstimate. Scott confirms the final tax with you.
          </p>
        </div>
      </div>
    </StepShell>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className={`flex items-center justify-between gap-2 ${bold ? "font-extrabold text-[var(--ssc-navy)]" : "text-[var(--ssc-ink-2)]"}`}>
      <span>{label}</span><span className="tabular-nums">{value}</span>
    </div>
  );
}
